import React, { useCallback, useEffect, useState } from 'react';
import { ExternalLink, LayoutTemplate, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  cmsLandingpageApi,
  WEBSITE_BASE_URL,
  type CmsLandingpage,
  type CmsLandingpageVarianten,
} from '../../../../lib/api/cms';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Badge } from '../../../../components/ui/badge';
import LoeschenMitGrundDialog from '../../../../components/ui/loeschen-mit-grund-dialog';
import LandingpageAuswertung from './LandingpageAuswertung';

/** „12 von 340 (3,5 %)“ — die Rate erst, wenn es etwas zu teilen gibt. */
function rate(anfragen: number, aufrufe: number): string {
  if (aufrufe === 0) return '–';
  return `${(Math.round((anfragen / aufrufe) * 1000) / 10).toLocaleString('de-DE')} %`;
}

/**
 * A/B-Test einer Landingpage: Original (Variante A) und Varianten mit Gewicht,
 * an/aus und den bisherigen Zahlen. Ausgelost wird je Seitenaufruf auf der
 * Website, ohne Cookie; gezählt werden Aufrufe und Probestunden-Anfragen als
 * Summen. Eine Variante startet als Kopie des Originals und wird im Editor
 * gestaltet und veröffentlicht, bevor sie mitlaufen kann.
 */
const LandingpageVarianten: React.FC<{ seite: CmsLandingpage; gestalten: (pageId: number) => void }> = ({ seite, gestalten }) => {
  const [daten, setDaten] = useState<CmsLandingpageVarianten | null>(null);
  const [legtAn, setLegtAn] = useState(false);
  const [neuName, setNeuName] = useState('');
  const [zuLoeschen, setZuLoeschen] = useState<number | null>(null);
  // Lädt die Auswertung nach jeder Änderung hier mit neu.
  const [stand, setStand] = useState(0);

  const laden = useCallback(async () => {
    try {
      setDaten(await cmsLandingpageApi.varianten(seite.id));
      setStand((s) => s + 1);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Varianten konnten nicht geladen werden');
    }
  }, [seite.id]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const ausfuehren = async (aktion: () => Promise<unknown>, erfolg?: string) => {
    try {
      await aktion();
      if (erfolg) toast.success(erfolg);
      await laden();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Das hat nicht geklappt');
    }
  };

  if (!daten) {
    return (
      <div className="flex justify-center py-8 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const laufend = daten.items.filter((v) => v.aktiv && v.hat_dokument && v.gewicht > 0);
  const summe = daten.original.gewicht + laufend.reduce((s, v) => s + v.gewicht, 0);
  const anteil = (gewicht: number, laeuft: boolean) => (laeuft && summe > 0 ? `${Math.round((gewicht / summe) * 100)} %` : '0 %');

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Jeder Aufruf von <code>/{seite.pfad}</code> zeigt eine Variante, ausgelost nach Gewicht — ohne Cookie, wer neu lädt, kann eine andere
        sehen. Gezählt werden Aufrufe und Probestunden-Anfragen. Eine neue Variante ist eine Kopie des Originals: im Editor gestalten,
        veröffentlichen, dann hier einschalten.
      </p>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr>
              <th className="py-2 pr-3 font-medium">Variante</th>
              <th className="py-2 pr-3 font-medium">Gewicht</th>
              <th className="py-2 pr-3 font-medium">Anteil</th>
              <th className="py-2 pr-3 font-medium">Aufrufe</th>
              <th className="py-2 pr-3 font-medium">Anfragen</th>
              <th className="py-2 pr-3 font-medium">Rate</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody className="divide-y">
            <tr>
              <td className="py-2 pr-3">
                <span className="font-medium">A · Original</span>
              </td>
              <td className="py-2 pr-3">
                <Input
                  aria-label="Gewicht von A"
                  type="number"
                  min={0}
                  max={1000}
                  className="h-8 w-20"
                  defaultValue={daten.original.gewicht}
                  onBlur={(e) => {
                    const wert = Number(e.target.value);
                    if (wert !== daten.original.gewicht) void ausfuehren(() => cmsLandingpageApi.update(seite.id, { gewicht: wert }));
                  }}
                />
              </td>
              <td className="py-2 pr-3">{anteil(daten.original.gewicht, true)}</td>
              <td className="py-2 pr-3">{daten.original.aufrufe}</td>
              <td className="py-2 pr-3">{daten.original.anfragen}</td>
              <td className="py-2 pr-3">{rate(daten.original.anfragen, daten.original.aufrufe)}</td>
              <td className="py-2" />
            </tr>
            {daten.items.map((v) => {
              const laeuft = laufend.includes(v);
              return (
                <tr key={v.id}>
                  <td className="py-2 pr-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">
                        {v.schluessel.toUpperCase()} · {v.name}
                      </span>
                      {!v.hat_dokument ? (
                        <Badge variant="secondary" size="sm">
                          Noch nicht veröffentlicht
                        </Badge>
                      ) : null}
                    </div>
                    <a
                      href={`${WEBSITE_BASE_URL}/${v.pfad}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
                    >
                      /{v.pfad} <ExternalLink className="h-3 w-3" />
                    </a>
                  </td>
                  <td className="py-2 pr-3">
                    <Input
                      aria-label={`Gewicht von ${v.schluessel.toUpperCase()}`}
                      type="number"
                      min={0}
                      max={1000}
                      className="h-8 w-20"
                      defaultValue={v.gewicht}
                      onBlur={(e) => {
                        const wert = Number(e.target.value);
                        if (wert !== v.gewicht) void ausfuehren(() => cmsLandingpageApi.varianteAendern(seite.id, v.id, { gewicht: wert }));
                      }}
                    />
                  </td>
                  <td className="py-2 pr-3">{anteil(v.gewicht, laeuft)}</td>
                  <td className="py-2 pr-3">{v.aufrufe}</td>
                  <td className="py-2 pr-3">{v.anfragen}</td>
                  <td className="py-2 pr-3">{rate(v.anfragen, v.aufrufe)}</td>
                  <td className="py-2">
                    <div className="flex items-center justify-end gap-2">
                      <label className="flex items-center gap-1.5 text-xs">
                        <input
                          type="checkbox"
                          checked={v.aktiv}
                          disabled={!v.hat_dokument && !v.aktiv}
                          title={!v.hat_dokument ? 'Erst im Editor veröffentlichen' : undefined}
                          onChange={(e) =>
                            void ausfuehren(
                              () => cmsLandingpageApi.varianteAendern(seite.id, v.id, { aktiv: e.target.checked }),
                              e.target.checked ? `${v.name} läuft mit` : `${v.name} pausiert`,
                            )
                          }
                        />
                        Läuft
                      </label>
                      <Button type="button" variant="outline" size="sm" onClick={() => gestalten(v.page_id)}>
                        <LayoutTemplate className="mr-1.5 h-4 w-4" /> Gestalten
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        aria-label={`${v.name} löschen`}
                        onClick={() => setZuLoeschen(v.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          aria-label="Name der neuen Variante"
          className="h-9 max-w-xs"
          placeholder="z. B. Kürzerer Hero"
          value={neuName}
          onChange={(e) => setNeuName(e.target.value)}
        />
        <Button
          type="button"
          variant="outline"
          loading={legtAn}
          onClick={async () => {
            setLegtAn(true);
            await ausfuehren(() => cmsLandingpageApi.varianteAnlegen(seite.id, neuName), 'Variante angelegt — jetzt gestalten');
            setNeuName('');
            setLegtAn(false);
          }}
        >
          <Plus className="mr-2 h-4 w-4" /> Variante anlegen
        </Button>
      </div>

      {daten.items.length > 0 ? (
        <div className="border-t pt-4">
          <LandingpageAuswertung seiteId={seite.id} aenderung={stand} nachUebernahme={() => void laden()} />
        </div>
      ) : null}

      <LoeschenMitGrundDialog
        offen={zuLoeschen !== null}
        titel="Variante löschen? Ihre bisherigen Zahlen bleiben in der Auswertung."
        onAbbrechen={() => setZuLoeschen(null)}
        onLoeschen={async (grund) => {
          const id = zuLoeschen;
          setZuLoeschen(null);
          if (id !== null) await ausfuehren(() => cmsLandingpageApi.varianteLoeschen(seite.id, id, grund), 'Variante gelöscht');
        }}
      />
    </div>
  );
};

export default LandingpageVarianten;
